/**
 * Main Layout Component
 * Left sidebar with conversation list, right side with full-width conversation view
 * URL-driven navigation: /w/:workspaceId/c/:conversationId and /w/:workspaceId/c/:conversationId/chat
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { LayoutGroup } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useConversation } from '@/app/providers/ConversationContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { useOnboardingActive } from '@/app/providers/OnboardingActiveContext';
import { useReviseSession } from '@/app/providers/ReviseSessionContext';
import { useSession } from '@/features/auth/hooks/useSession';
import ConversationSidebar from './Sidebar';
import { SidebarRail } from './SidebarRail';
import ChatBox from '@/features/chat/components/ChatBox';
import type { ChatBoxHandle } from '@/features/chat/components/ChatBox';
import { ProfileModal } from './ProfileModal';
import { RemoveDemoDataButton } from './RemoveDemoDataButton';
import { MQ, useIsCompactLayout, useMediaQuery } from '@/common/hooks';
import { confirmDialog } from '@/common/components/ConfirmDialog';
import { hapticMedium } from '@/lib/capacitor';
import { WorkspaceOptionsModal } from './WorkspaceOptionsModal';
import { ArchivedModal } from './ArchivedModal';

import { IntegrationsModal } from './IntegrationsModal';
import { DataBackupsModal } from './DataBackupsModal';
import { TeamModal } from './TeamModal';
import { BriefDockFlight } from '@/features/daily-brief/BriefDockFlight';
import { useBriefDock } from '@/app/providers/BriefDockContext';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { DailySummaryModal } from './DailySummaryModal';
import { useDailyBrief } from '@/app/providers/DailyBriefContext';
import { computeBriefPanelRect, computeBriefSplitRect } from '@/features/daily-brief/brief-dock-geometry';
import { HeaderBriefLauncher } from './HeaderBriefLauncher';
import { ThemeModal } from './ThemeModal';
import { HeaderChatLauncher } from './HeaderChatLauncher';
import { HeaderOpenChatButton } from './HeaderOpenChatButton';
import { NotificationsProvider } from '@/features/notifications/NotificationsProvider';
import { OAuthConsentModal } from '@/features/oauth/components/OAuthConsentModal';
import { useActivitiesBadge } from '@/features/activities/useActivities';
import { useEmailApprovals } from '@/features/activities/useEmailApprovals';
import { ActivityCenter } from '@/features/activities/ActivityCenter';
import { NotificationToastHost } from '@/features/activities/NotificationToastHost';
import { AgentRunNotifier } from '@/features/activities/AgentRunNotifier';
import type { ContainerAccess } from '@/types/session';
import type { Conversation } from '@/types/conversations';
import { isSummaryModuleType } from '@/types/modules';
import { buildConversationPath, resolveWorkspaceId } from '@/lib/workspace-routing';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useReorderConversationsMutation } from '@/features/conversations/hooks/useReorderConversationsMutation';
import { queryKeys } from '@/lib/query/keys';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { useHandleCreateConversation } from './useHandleCreateConversation';
import { useChatNotificationExclusivity } from './useChatNotificationExclusivity';
import { useReviseSessionReconciler } from './useReviseSessionReconciler';
import { computeBriefPlacement } from './briefPlacement';
import { WelcomePane } from './WelcomePane';
import { pluginsStillLoadingHold, paneIntentHoldsUrl } from './moduleUrlGuard';
import { consumePendingDeepLink, type PendingDeepLink } from '@/lib/pending-deep-link';
import { api } from '@/lib/api';
import { listStandingAgents } from '@/lib/api/endpoints/agent-trust';
import { readDailyBriefAutoOpen, decideAutoOpen, LS_LAST_SHOWN } from '@/features/daily-brief/autoOpen';
import {
  addWindowListener,
  conversationEvents,
  removeWindowListener,
} from '@/lib/conversation-events';
import './MainLayout.css';

export default function MainLayout({ onboardingActive = false }: { onboardingActive?: boolean } = {}) {
  const { activeConversation, setActiveConversation, isLoading, allConversations, pluginInstances, pluginInstancesLoaded, deleteConversation } = useConversation();
  const queryClient = useQueryClient();
  const { token, isReadOnly, profile, selectedContainer } = useSession();
  const { conversationId: conversationIdParam, view, moduleId: moduleIdParam } = useParams<{ conversationId?: string; view?: string; moduleId?: string }>();
  // Module-pane authority (ADR 028) — the URL guard below holds (instead of
  // redirecting) while the pane shows live intent for the URL's module. The
  // hold is computed from getPaneState() (the eager mirror): a rail click's
  // React flip rides an async View Transition, so the render-snapshot values
  // are stale exactly when the guard runs against the freshly-navigated URL.
  // paneModuleId/isPaneVisible stay in the dep array so the effect re-evaluates
  // when the flip commits or the missing-module cleanup releases the hold.
  const { paneModuleId, isPaneVisible, getPaneState } = useModulePane();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsCompactLayout();
  // The Daily Brief opens in one of three placements:
  //   • desktop solo chat (no module PANE) → top-1/3 card above the chat (the
  //                                  chat eases down to the bottom 2/3)
  //   • desktop, module pane open OR an active Revise session → PORTAL into the
  //                                  chat-box grid (module-open = top third of the
  //                                  right column; revise = the 50/50 left column,
  //                                  15-revise-split.css)
  //   • mobile (≤767px)            → the centered modal (fallback)
  //
  // CRITICAL: "module open" means the module PANE IS VISIBLE, not "the URL has
  // /mod/:moduleId" — closing the pane (X) only flips ChatBox-internal state
  // and deliberately KEEPS the URL's moduleId (it remembers the module for
  // reopening). Gating on the URL portaled the Brief into a chat-box that
  // wasn't a split grid anymore, so it rendered as a plain block BELOW the
  // chat at full width. ChatDockContext.isSplitViewActive is the true signal:
  // ChatBox publishes it from (module pane visible && desktop ≥768px). The
  // companion-vs-split decision is the pure, table-tested computeBriefPlacement
  // (briefPlacement.ts), evaluated once below — the two are EXCLUSIVE.
  const isDesktopViewport = useMediaQuery(MQ.desktopSplit);
  const { isSplitViewActive } = useChatDock();
  // Revise-with-AI 50/50: the sole state where chat AND notifications are both
  // open (the W6 coordinator yields). It reuses the split-brief portal below.
  const { reviseActive } = useReviseSession();
  // Chat-window onboarding: the desktop rail is NO LONGER hidden during the
  // guided tour — the rail and chat load together at shell-readiness, the same
  // way a returning user's shell reveals (App.tsx holds the loader until
  // `modulesReady` for onboarding too, so the rail's first paint is already the
  // user's final module set). `effectiveOnboardingActive` now only gates the
  // MOBILE conversation drawer (there is no persistent rail on mobile). It reads
  // the CONTEXT and-ed with the PROP so a failed post-onboarding refreshSession
  // can't strand the drawer suppressed: the context flips false synchronously
  // when the tour ends (endOnboardingActive), independent of the completion
  // latch. On the returning-user path both are false, so this is a no-op.
  const { onboardingActive: onboardingActiveCtx } = useOnboardingActive();
  const effectiveOnboardingActive = onboardingActive && onboardingActiveCtx;

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isWorkspaceOptionsOpen, setIsWorkspaceOptionsOpen] = useState(false);
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);

  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false);
  const [isDataBackupsOpen, setIsDataBackupsOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [showMobileConversationDropdown, setShowMobileConversationDropdown] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showGoogleSyncModal, setShowGoogleSyncModal] = useState(false);
  // Daily Brief dock state machine ("the panel docks into / out of the header
  // bell"; mirrors the chat dock). The bare `briefOpen` boolean below gates every
  // render branch, while the underlying state sequences the open/close flight.
  const {
    dockState: briefDockState,
    openBrief,
    closeBrief,
    registerPanelRectProvider,
    setPlacementActive,
    flightRect: briefFlightRect,
    finishOpen: finishBriefOpen,
    pendingLandingReveal: briefLandingReveal,
    ackLandingReveal: ackBriefLandingReveal,
  } = useBriefDock();
  // The panel stays mounted through the closing flight ('docking') so it can
  // fade out under the traveling glass; "open" for render-gating purposes means
  // any non-closed state.
  const briefOpen = briefDockState !== 'closed';
  // Daily Brief: controlled modal surface (driven by useDailyBrief / DailyBriefProvider).
  // This is a SEPARATE surface from the BriefDock/ActivityCenter dock slots.
  const dailyBrief = useDailyBrief();
  // Workstream 6: chat ↔ notifications strict single-open. One coordinator, reacting to dock-state
  // transitions (so programmatic opens are covered), yielding during an active Revise session.
  useChatNotificationExclusivity();
  // Workstream 7 (§7.5): end the Revise session on EVERY terminal path — Send, Save & send, expiry,
  // decided-from-another-surface, AND close-chat/switch-conversation (#5424) — not just Discard.
  // MainLayout is the stable owner (always mounted, already holds the session flag and the
  // session token/workspaceId). Without this, Send strands `reviseActive` true against a decided
  // approval (or a closed/abandoned revise chat) and permanently defeats strict single-open for
  // the rest of the session.
  useReviseSessionReconciler(token, workspaceId);

  // NOTE: the chat dock and the Brief dock are deliberately INDEPENDENT — in
  // the split, docking the chat keeps the Brief open in its top-1/3 cell (the
  // right column stays at width while a .chat-brief-pane is present; see the
  // .chat-pane-collapsed:has(.chat-brief-pane) rule in ChatBox.css), and the
  // chat's own dock flight lands on the bottom-2/3 rect (useChatDockWiring).
  // The Brief panel card is the morph's panel-side endpoint — its rect is
  // measured synchronously by the dock context before a transition (framer
  // remembers the bell's rect from the unmounting HeaderBriefLauncher, so only
  // the panel rect is needed). The bell itself lives in HeaderBriefLauncher.
  const briefPanelCardRef = useRef<HTMLDivElement | null>(null);
  // The chat-panel container is always mounted in the solo placement; on OPEN
  // (card not mounted yet) the panel's resting rect is computed from it.
  const briefPanelContainerRef = useRef<HTMLDivElement | null>(null);
  // Module-pane split OR active Revise session: render the Brief in MainLayout
  // (state lives here) but PORTAL it into the chat-box grid — mirrors how the
  // module pane portals into the same root. The chat-box grid places it
  // (module-open via :has(.chat-brief-pane); revise via .chat-box.is-revise-split).
  // showBriefCompanion is derived here too so the two placements stay mutually
  // exclusive. isSplitViewActive already implies ≥768px; reviseActive does not,
  // so computeBriefPlacement desktop-gates the revise branch (mobile keeps the
  // centered modal, unchanged).
  const { showBriefCompanion, wantSplitBrief } = computeBriefPlacement({
    briefOpen,
    isSplitViewActive,
    isDesktopViewport,
    reviseActive,
  });
  const [splitBriefHost, setSplitBriefHost] = useState<HTMLElement | null>(null);
  // Layout effect, REQUIRED: a module open is wrapped in startViewTransition
  // (pane-view-transition.ts) whose update callback flushSyncs the whole chain
  // — chat flip → isSplitViewActive (layout effect in useChatDockWiring) →
  // this re-render. A passive effect here resolved the host only AFTER the
  // transition snapshotted "new", so the Brief existed NOWHERE in that
  // snapshot (solo card unmounted, split portal still hostless): the VT faded
  // the old Brief out and the card popped in post-paint with a fresh entrance
  // — "appearing from a different origin" instead of pushing aside with the
  // chat. A layout effect's setState flushes synchronously pre-paint, so the
  // portal mounts inside the same flush and daily-brief-pane pairs old↔new.
  useLayoutEffect(() => {
    if (!wantSplitBrief) {
      setSplitBriefHost(null);
      return;
    }
    // The chat-box root exists whenever the split view is active.
    setSplitBriefHost(document.getElementById('chat-box-root'));
  }, [wantSplitBrief]);
  const [themeOpen, setThemeOpen] = useState(false);
  const { reviewCount } = useActivitiesBadge(workspaceId);
  // Pending email-draft approvals aren't counted by /activities/badge, so the closed
  // bell wouldn't badge when a draft is waiting. Fold them in here — same sum the open
  // Activity Center applies to its Review tab (ActivityCenter reviewBadge). Polls ~15s.
  const { items: emailDrafts } = useEmailApprovals();
  const bellReviewCount = (reviewCount ?? 0) + emailDrafts.length;

  // Standing-agents query: fetch the daily_briefing config for the auto-open time gate.
  const { data: standing, isFetched: standingFetched } = useQuery({
    queryKey: ['standing-agents', workspaceId],
    queryFn: () => listStandingAgents(token!, workspaceId!),
    enabled: Boolean(token && workspaceId),
    staleTime: 5 * 60_000,
  });
  const briefCfg = standing?.items?.find(
    (a) => (a.triggerConfig as { kind?: string } | undefined)?.kind === 'daily_briefing',
  );

  // Chat-dock landing ripple: when the chat panel finishes docking into the
  // header, give the neighboring icons a brief staggered nudge (CSS-driven
  // via the .dock-ripple class; see MainLayout.css).
  const [dockRippleActive, setDockRippleActive] = useState(false);
  const dockRippleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ONE shared ripple timer for both dock systems (chat launcher + Brief
  // flight). They land into the same .app-header-actions group, so if both fire
  // in the same ~600ms window two competing timers would clobber each other —
  // resetting the single timer keeps the neighbor-nudge consistent.
  const handleChatDockLanded = useCallback(() => {
    setDockRippleActive(true);
    if (dockRippleTimerRef.current) clearTimeout(dockRippleTimerRef.current);
    dockRippleTimerRef.current = setTimeout(() => setDockRippleActive(false), 600);
  }, []);
  useEffect(() => () => {
    if (dockRippleTimerRef.current) clearTimeout(dockRippleTimerRef.current);
  }, []);

  // Kept current for the panel-rect provider, which reads the placement without
  // re-registering on every open/close. Mirrors isSplitViewActive (the REAL
  // module-pane visibility), not the URL's moduleId — see the gating note above.
  const splitActiveRef = useRef(isSplitViewActive);
  useEffect(() => {
    splitActiveRef.current = isSplitViewActive;
  }, [isSplitViewActive]);

  // Brief dock: register the panel-rect measurer the context calls synchronously
  // before flipping into a transitional state (framer remembers the bell's rect
  // from the unmounting launcher, so only the panel rect is provided here). On
  // CLOSE the card is mounted — measure it live. On OPEN it isn't yet (its
  // entrance IS the flight), so compute the resting rect for the current
  // placement — the top-third of the chat-box right column (split) or of the
  // whole chat panel (solo). Null-guards so a first-paint race falls back to an
  // instant swap (no wedged flight).
  useEffect(() => {
    registerPanelRectProvider(() => {
      const card = briefPanelCardRef.current;
      if (card) {
        const r = card.getBoundingClientRect();
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      }
      if (typeof window === 'undefined') return null;
      if (splitActiveRef.current) {
        const root = document.getElementById('chat-box-root');
        if (!root) return null;
        const r = root.getBoundingClientRect();
        return computeBriefSplitRect(
          { top: r.top, left: r.left, width: r.width, height: r.height },
          window.innerWidth,
          // Workstream 6 (strict single-open): an open Brief always docks the chat and fills the
          // FULL right column, so the open flight targets the full-column rect.
          true,
        );
      }
      const container = briefPanelContainerRef.current;
      if (!container) return null;
      const r = container.getBoundingClientRect();
      return computeBriefPanelRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
    return () => registerPanelRectProvider(null);
  }, [registerPanelRectProvider]);

  // Publish whether a dockable DESKTOP placement is live (solo companion OR the
  // module-open split, both ≥768px). Gated on width — not the open state — so
  // BOTH directions morph. The mobile modal (≤767px) keeps this false →
  // instant swap. Flipping it false also settles any in-flight transition
  // synchronously (the panel host can unmount when the viewport narrows). The
  // bell's own focus + landing ripple are owned by HeaderBriefLauncher (it
  // focuses itself on landing, mirroring the chat dock).
  useEffect(() => {
    setPlacementActive(isDesktopViewport);
  }, [setPlacementActive, isDesktopViewport]);

  // Bell click toggles the dock: open from 'closed', close from 'open'. Used by
  // the mobile bell (≤767px) — the desktop morph bell (HeaderBriefLauncher)
  // only ever opens (it's gone while open).
  const handleToggleBrief = useCallback(() => {
    if (briefDockState === 'closed') openBrief();
    else if (briefDockState === 'open') closeBrief();
  }, [briefDockState, openBrief, closeBrief]);

  // State A: once-per-day, time-gated auto-open of the Daily Brief. The decision
  // is the pure, table-tested decideAutoOpen() — this effect only gathers inputs
  // and acts on the result. briefCheckedRef gates one decision per session; it is
  // set ONLY on a settled outcome ('markShown'/'open'), never while 'wait'ing, so
  // the not-onboarded / time-not-passed / dock-open cases retry on later renders.
  const briefCheckedRef = useRef(false);
  useEffect(() => {
    if (!token || !profile || briefCheckedRef.current || typeof window === 'undefined') return;
    if (!standingFetched) return; // wait for daily_briefing config before deciding — do NOT mark the ref
    // Use profile.onboardingComplete — the legacy `onboardingCompleted` field can
    // read false for an already-onboarded user, wrongly defaulting auto-open OFF.
    const deliveryTime = (briefCfg?.preferences as { deliveryTime?: string } | undefined)?.deliveryTime;
    const schedule = (briefCfg?.triggerConfig as { schedule?: { hour?: number; minute?: number } } | undefined)?.schedule;
    const { action, todayKey } = decideAutoOpen({
      now: new Date(),
      onboardingComplete: profile.onboardingComplete,
      autoOpen: readDailyBriefAutoOpen(profile.onboardingComplete),
      deliveryTime,
      schedule,
      lastShown: localStorage.getItem(LS_LAST_SHOWN),
      dockClosed: briefDockState === 'closed',
    });
    if (action === 'wait') return; // conditions not yet met — do NOT mark the ref
    if (action === 'markShown') { briefCheckedRef.current = true; return; }
    // action === 'open'
    briefCheckedRef.current = true;
    dailyBrief.open();
    localStorage.setItem(LS_LAST_SHOWN, todayKey);
  }, [token, profile, briefCfg, standingFetched, briefDockState, dailyBrief]);

  // Mobile conversation edit mode state
  const [isMobileEditMode, setIsMobileEditMode] = useState(false);

  // #2721 safety net: if `pluginInstancesLoaded` stays false for >2s
  // (network stall, slow API), release the URL guard so the user isn't
  // stuck on a frozen panel. Resets on conversation switch (the effect
  // re-arms whenever activeConversation.id changes).
  const [pluginGuardExpired, setPluginGuardExpired] = useState(false);
  useEffect(() => {
    setPluginGuardExpired(false);
    if (!activeConversation?.id) return;
    if (pluginInstancesLoaded) return;
    const timer = window.setTimeout(() => setPluginGuardExpired(true), 2000);
    return () => window.clearTimeout(timer);
  }, [activeConversation?.id, pluginInstancesLoaded]);

  // Swipe-to-sidebar gesture
  const swipeSidebarRef = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    tracking: boolean;
    direction: 'open' | 'close' | null;
  } | null>(null);
  const [sidebarDragX, setSidebarDragX] = useState<number | null>(null);
  const SIDEBAR_WIDTH = 260;
  const SWIPE_EDGE_ZONE = 20;
  const SWIPE_COMMIT_RATIO = 0.3;
  const SWIPE_VELOCITY_THRESHOLD = 0.5; // px/ms

  // Toggle body class so CSS can lower chat z-index when sidebar is open
  useEffect(() => {
    if (showMobileSidebar) {
      document.body.classList.add('mobile-sidebar-open');
    } else {
      document.body.classList.remove('mobile-sidebar-open');
    }
    return () => document.body.classList.remove('mobile-sidebar-open');
  }, [showMobileSidebar]);

  // Close mobile sidebar when a module is opened from SidebarRail
  useEffect(() => {
    if (!isMobile) return;
    const handler = () => setShowMobileSidebar(false);
    window.addEventListener('xavior:force-module-focus', handler);
    return () => window.removeEventListener('xavior:force-module-focus', handler);
  }, [isMobile]);

  // Reorder mutation hook with optimistic updates
  const reorderMutation = useReorderConversationsMutation();

  const handleCreateConversation = useHandleCreateConversation();

  // ── Generic deep link: open module by type or navigate to conversation ──
  const deepLinkHandledRef = useRef(false);
  const deepLinkPendingRef = useRef<PendingDeepLink | null>(null);
  useEffect(() => {
    if (isLoading || deepLinkHandledRef.current) return;

    // On first call, consume from localStorage and cache for retries
    if (!deepLinkPendingRef.current) {
      const pending = consumePendingDeepLink();
      if (!pending) return;
      deepLinkPendingRef.current = pending;
    }

    const pending = deepLinkPendingRef.current;

    if (pending.type === 'conversation' && pending.conversationId) {
      navigate(buildConversationPath(workspaceId, pending.conversationId, { view: 'chat' }));
      deepLinkHandledRef.current = true;
      return;
    }

    if (pending.type === 'module' && pending.moduleType) {
      // Find first module of this type and navigate to it
      (async () => {
        try {
          const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {
            type: pending.moduleType,
            includeArchived: false,
          });
          if (modules.length > 0) {
            const moduleId = modules[0].id;
            const targetConversationId = activeConversation?.id ?? allConversations[0]?.id;
            if (targetConversationId) {
              navigate(buildConversationPath(workspaceId, targetConversationId, { moduleId }));
              window.dispatchEvent(new CustomEvent('xavior:force-module-focus', { detail: { moduleId } }));
            }
          }
          deepLinkHandledRef.current = true;
        } catch (err) {
          console.warn('[MainLayout] Failed to handle module deep link:', err);
        }
      })();
    }
  }, [isLoading, navigate, token, workspaceId, activeConversation, allConversations]);

  // Touch drag reorder state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragTargetIndex, setDragTargetIndex] = useState<number | null>(null);
  const [tempConversationOrder, setTempConversationOrder] = useState<Conversation[] | null>(null);
  const dragStartY = useRef<number>(0);
  const dragCurrentConversation = useRef<string | null>(null);

  // Use temp order if available, otherwise use API-provided conversation order
  const sortedConversations = tempConversationOrder ?? allConversations;

  // Helper to check if two conversation arrays have the same order
  const sameOrderById = useCallback((a: Conversation[], b: Conversation[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].id !== b[i].id) return false;
    }
    return true;
  }, []);

  // Only clear tempConversationOrder once the query cache has caught up to the committed order
  useEffect(() => {
    if (!tempConversationOrder) return;
    if (sameOrderById(tempConversationOrder, allConversations)) {
      setTempConversationOrder(null);
    }
  }, [tempConversationOrder, allConversations, sameOrderById]);

  // Touch drag handlers
  const handleTouchDragStart = useCallback((index: number, y: number) => {
    hapticMedium();
    setDragIndex(index);
    setDragTargetIndex(index);
    dragStartY.current = y;
    dragCurrentConversation.current = sortedConversations[index]?.id ?? null;
  }, [sortedConversations]);

  const handleTouchDragMove = useCallback((y: number) => {
    if (dragIndex === null) return;
    // Find which conversation item the touch is over
    const elements = document.querySelectorAll('.mobile-conversation-selector-content .mobile-conversation-selector-option');
    for (let i = 0; i < elements.length; i++) {
      const rect = elements[i].getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        setDragTargetIndex(i);
        return;
      }
    }
  }, [dragIndex]);

  const handleTouchDragEnd = useCallback(() => {
    if (dragIndex !== null && dragTargetIndex !== null && dragIndex !== dragTargetIndex) {
      const conversations = tempConversationOrder ?? allConversations;
      const newOrder = [...conversations];
      const [moved] = newOrder.splice(dragIndex, 1);
      newOrder.splice(dragTargetIndex, 0, moved);

      // Keep the UI on the final order immediately (no visual fallback)
      setTempConversationOrder(newOrder);

      // Optimistic cache update happens in onMutate (immediate)
      reorderMutation.mutate(newOrder.map(h => h.id), {
        onError: () => {
          // On error, clear temp order to show rolled-back cache state
          console.error('[Mobile Reorder] Failed to save conversation order');
          setTempConversationOrder(null);
        },
      });
    }
    setDragIndex(null);
    setDragTargetIndex(null);
    dragCurrentConversation.current = null;
  }, [dragIndex, dragTargetIndex, sortedConversations, reorderMutation]);

  // Archive handler for mobile edit mode
  const handleMobileArchive = useCallback((conversation: Conversation) => {
    void (async () => {
      if (!(await confirmDialog({ message: `Archive "${conversation.name}"?`, confirmLabel: 'Archive' }))) return;
      try {
        await api.workspaces.updateConversation(token ?? null, workspaceId, conversation.id, {
          archived: true,
        });
        if (activeConversation?.id === conversation.id) {
          setActiveConversation(null);
        }
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
      } catch (error) {
        console.error('[MainLayout] Failed to archive conversation:', error);
      }
      // Exit edit mode if only 1 conversation left after archiving
      if (sortedConversations.length <= 2) {
        setIsMobileEditMode(false);
      }
    })();
  }, [token, workspaceId, activeConversation?.id, sortedConversations.length]);

  // Exit edit mode when panel closes
  useEffect(() => {
    if (!showMobileConversationDropdown) {
      setIsMobileEditMode(false);
    }
  }, [showMobileConversationDropdown]);
  const mobileConversationDropdownRef = useRef<HTMLDivElement>(null);
  const mobileConversationPanelRef = useRef<HTMLElement>(null);
  const chatBoxRef = useRef<ChatBoxHandle>(null);

  // Show name prompt if user has no name set (cannot be dismissed)
  // Skip during onboarding — the chat flow collects the name instead


  // Listen for conversation options events to show workspace options modal.
  useEffect(() => {
    const handleOpenConversationOptions = () => {
      setIsWorkspaceOptionsOpen(true);
    };

    addWindowListener(conversationEvents.openOptions, handleOpenConversationOptions);
    return () => {
      removeWindowListener(conversationEvents.openOptions, handleOpenConversationOptions);
    };
  }, []);

  // Listen for openArchivedModal + openWorkspaceOptions events. The latter
  // lets the mobile sidebar profile-tap trigger the unified WorkspaceOptions
  // sheet without prop-drilling a handler through the tree.
  useEffect(() => {
    const handleOpenArchived = () => setIsArchivedModalOpen(true);
    const handleOpenWorkspaceOptions = () => setIsWorkspaceOptionsOpen(true);
    window.addEventListener('openArchivedModal', handleOpenArchived);
    window.addEventListener('openWorkspaceOptions', handleOpenWorkspaceOptions);
    return () => {
      window.removeEventListener('openArchivedModal', handleOpenArchived);
      window.removeEventListener('openWorkspaceOptions', handleOpenWorkspaceOptions);
    };
  }, []);



  // Listen for openDataBackupsModal event
  useEffect(() => {
    const handler = () => setIsDataBackupsOpen(true);
    window.addEventListener('openDataBackupsModal', handler);
    return () => window.removeEventListener('openDataBackupsModal', handler);
  }, []);

  // Listen for openTeamModal event (with optional tab)
  const [teamModalInitialTab, setTeamModalInitialTab] = useState<'my-team' | 'shared-workspaces' | 'settings'>('my-team');
  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail?.tab;
      setTeamModalInitialTab(tab === 'shared-workspaces' ? 'shared-workspaces' : tab === 'settings' ? 'settings' : 'my-team');
      setIsTeamModalOpen(true);
    };
    window.addEventListener('openTeamModal', handler);
    return () => window.removeEventListener('openTeamModal', handler);
  }, []);

  // Listen for openIntegrationsModal event (from mobile hamburger / workspace options)
  useEffect(() => {
    const handler = () => setIsIntegrationsOpen(true);
    window.addEventListener('openIntegrationsModal', handler);
    return () => window.removeEventListener('openIntegrationsModal', handler);
  }, []);

  // Listen for openGoogleSyncModal event (from Gmail module connect buttons)
  useEffect(() => {
    const handler = () => setShowGoogleSyncModal(true);
    window.addEventListener('openGoogleSyncModal', handler);
    return () => window.removeEventListener('openGoogleSyncModal', handler);
  }, []);

  // Listen for triggerGoogleOAuth — skip modal, go straight to OAuth
  useEffect(() => {
    const handler = async (e: Event) => {
      const authToken = token;
      const convId = activeConversation?.id;
      if (!authToken || !convId) return;
      // Module-scoped capability (ADR 017 D4 progressive consent): a module
      // connecting Google for a non-base capability (e.g. Drive) rides it in
      // the event detail so the backend requests that scope too. Bare dispatches
      // (Email/Calendar, in the base) carry no detail → base scopes, unchanged.
      const capability = (e as CustomEvent<{ capability?: string }>).detail?.capability;
      try {
        // Optimistic loading: mark OAuth in progress BEFORE leaving the app.
        // When the user lands back here post-OAuth, EmailPanel reads this flag
        // and immediately renders the syncing spinner — no blank flash.
        try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
        // No integrationId → FLOW A (enroll/reconnect a Google account): the
        // backend keeps prompt=consent so the account picker shows.
        await connectProvider({ provider: 'google', authToken, conversationId: convId, capability });
      } catch {
        // If the auth API call failed, clear the flag so we don't lie about state
        try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ }
        console.error('Failed to connect to Google');
      }
    };
    window.addEventListener('triggerGoogleOAuth', handler);
    return () => window.removeEventListener('triggerGoogleOAuth', handler);
  }, [token, activeConversation?.id]);

  // Centralized post-connect refetch (#3382). App.tsx's OAuth-return handler
  // dispatches 'xavior:integration-connected' on every successful connect (connect
  // is a same-window redirect now). Invalidate the email/gmail/integration query
  // groups plus objects/google-accounts/modules so the just-connected account's
  // data refetches immediately. Mirrors the invalidation block in
  // useOnboardingManager.handleDemoLoad.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({
        predicate: (q) => {
          const key = JSON.stringify(q.queryKey);
          return key.includes('email') || key.includes('gmail') || key.includes('integration');
        },
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.googleAccounts.all });
      // Drive source-picker account lists (Google + Microsoft). Plain-array keys,
      // not caught by the predicate above. Refetch so a just-connected account
      // appears in the dropdown, and so an in-place "Enable Drive" re-grant flips
      // driveWritable → true (collapsing the re-consent prompt) without a reload.
      queryClient.invalidateQueries({ queryKey: ['drive-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['onedrive-accounts'] });
    };
    window.addEventListener('xavior:integration-connected', handler);
    return () => window.removeEventListener('xavior:integration-connected', handler);
  }, [queryClient]);

  // Set selected module from URL (route param or query param for backward compatibility)
  useEffect(() => {
    if (!activeConversation) return;

    // ✅ Critical: don't auto-navigate based on a stale activeConversation during conversation switches
    // If URL says conversation B but state still says conversation A, wait for ConversationRouteSync to catch up
    if (conversationIdParam && activeConversation.id !== conversationIdParam) return;

    // Use route param if available, otherwise fall back to query param
    const moduleIdFromUrl = moduleIdParam || new URLSearchParams(location.search).get('moduleId');

    // Determine whether the URL's moduleId actually matches a live module/plugin.
    // If it does — remember it and stay put.
    // If it doesn't (stale URL, module was archived or deleted) — fall through
    // to the default-selection branch below so we redirect to a valid module
    // instead of silently leaving a broken URL in the bar.
    const moduleIdMatches = moduleIdFromUrl
      ? (moduleIdFromUrl.startsWith('plugin:')
          ? pluginInstances.some(instance => instance.id === moduleIdFromUrl.replace('plugin:', ''))
          : (activeConversation.modules || []).some(
              mod => !mod.archived && !isSummaryModuleType(mod.type) && mod.id === moduleIdFromUrl
            ))
      : false;

    // #2721: Avoid jumping to a "random" (sortedModules[0]) module when the
    // URL points at a plugin (Gmail / Drive / etc.) but `pluginInstances`
    // hasn't finished loading yet. Without this guard, the post-OAuth or
    // post-disconnect refetch cycle briefly returns an empty plugin list,
    // moduleIdMatches drops to false, and the effect below redirects to the
    // first conversation module — leaving the user stranded on Events when
    // they were acting on Gmail.
    //
    // Earlier this guard keyed on `pluginInstances.length === 0`, which
    // deadlocked any conversation that legitimately has zero plugin
    // instances (the URL was held forever and the user got stuck on a blank
    // panel). We now key on `pluginInstancesLoaded` — a sentinel that flips
    // true once refreshPluginInstances() resolves (success or error). A
    // legitimately-empty list now distinguishes itself from "in-flight" and
    // falls through to default-module fallback.
    //
    // Belt-and-suspenders: if `pluginInstancesLoaded` somehow stayed false
    // for >2s (network stall, slow API), the `pluginGuardExpired` ref-based
    // timer trips and we release the hold anyway. The user lands on the
    // default module rather than a frozen panel.
    const pluginsStillLoading = pluginsStillLoadingHold({
      moduleIdFromUrl,
      pluginInstancesLoaded,
      pluginGuardExpired,
    });
    // ADR 028: the pane authority shows live intent for exactly this URL's
    // module (a just-clicked module whose conversation-panel attach is still
    // in flight, so it's not in activeConversation.modules yet). Hold the URL
    // — redirecting here bounced the click away and the bounce's module.focus
    // outranked the click's late attach-focus. If the attach fails, the
    // missing-module cleanup closes the pane and releases this hold. Read the
    // EAGER pane state: the click's React flip may still be riding the View
    // Transition when this effect runs. Provenance-gated: a pure URL seed
    // (stale bookmark) never holds.
    const pane = getPaneState();
    const paneHoldsUrl = paneIntentHoldsUrl({
      moduleIdFromUrl,
      moduleIdMatches,
      paneModuleId: pane.moduleId,
      isPaneVisible: pane.visible,
      paneTargetIsIntent: pane.targetIsIntent,
    });

    if (moduleIdFromUrl && moduleIdMatches) {
      // Remember this module for this conversation (localStorage so it
      // survives hard refreshes where sessionStorage is wiped)
      try { localStorage.setItem(`lastModule:${activeConversation.id}`, moduleIdFromUrl); } catch { /* best-effort */ }
      sessionStorage.setItem(`lastModule:${activeConversation.id}`, moduleIdFromUrl);
    } else if (pluginsStillLoading || paneHoldsUrl) {
      // URL has plugin:xxx but plugin instances haven't loaded — hold the URL
      // and wait for the next render. Will resolve to a match once
      // refreshPluginInstances() settles, or the 2s safety timer expires.
      // (Or: the pane authority is mid-open on this exact module — see above.)
      return;
    } else if (!view && activeConversation.modules && activeConversation.modules.length > 0) {
      // Check if we have a remembered module for this conversation. Prefer
      // localStorage (survives hard refresh / browser restart) over
      // sessionStorage (tab-scoped).
      const rememberedModule = (() => {
        try { return localStorage.getItem(`lastModule:${activeConversation.id}`) || sessionStorage.getItem(`lastModule:${activeConversation.id}`); }
        catch { return sessionStorage.getItem(`lastModule:${activeConversation.id}`); }
      })();

      const sortedModules = activeConversation.modules
        .filter(mod => !mod.archived && !isSummaryModuleType(mod.type))
        .sort((a, b) => a.position - b.position);

      let targetModuleId: string;

      if (rememberedModule) {
        // Verify the remembered module still exists
        const stillExists = rememberedModule.startsWith('plugin:')
          ? pluginInstances.some(instance => instance.id === rememberedModule.replace('plugin:', ''))
          : sortedModules.some(mod => mod.id === rememberedModule);

        targetModuleId = stillExists ? rememberedModule : sortedModules[0]?.id;
      } else {
        targetModuleId = sortedModules[0]?.id;
      }

      if (targetModuleId) {
        navigate(
          buildConversationPath(workspaceId, activeConversation.id, {
            moduleId: targetModuleId,
            search: location.search,
          }),
          { replace: true },
        );
      }
    }
  }, [conversationIdParam, activeConversation?.id, moduleIdParam, location.search, view, pluginInstances, pluginInstancesLoaded, pluginGuardExpired, paneModuleId, isPaneVisible, navigate, workspaceId]);

  // Backward compatibility: redirect old ?moduleId= URLs to new /mod/:moduleId paths
  useEffect(() => {
    // Same guard: don't redirect if URL conversation doesn't match state conversation
    if (conversationIdParam && activeConversation?.id !== conversationIdParam) return;

    const queryModuleId = new URLSearchParams(location.search).get('moduleId');
    if (queryModuleId && !moduleIdParam && activeConversation?.id) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete('moduleId');
      const queryString = newParams.toString();
      navigate(
        buildConversationPath(workspaceId, activeConversation.id, {
          moduleId: queryModuleId,
          search: queryString ? `?${queryString}` : '',
        }),
        { replace: true },
      );
    }
  }, [conversationIdParam, activeConversation?.id, moduleIdParam, location.search, navigate, workspaceId]);

  const handleConversationClick = () => {
    setShowMobileSidebar(false);
  };

  const handleSwipeTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    const isFromLeftEdge = touch.clientX <= SWIPE_EDGE_ZONE;
    const isOnOpenSidebar = showMobileSidebar && touch.clientX <= SIDEBAR_WIDTH;

    if (!isFromLeftEdge && !isOnOpenSidebar) return;

    swipeSidebarRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      tracking: false,
      direction: showMobileSidebar ? 'close' : 'open',
    };
  }, [isMobile, showMobileSidebar]);

  const handleSwipeTouchMove = useCallback((e: React.TouchEvent) => {
    const state = swipeSidebarRef.current;
    if (!state) return;

    const touch = e.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;

    // If vertical movement exceeds horizontal, cancel tracking (user is scrolling)
    if (!state.tracking && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      swipeSidebarRef.current = null;
      setSidebarDragX(null);
      return;
    }

    // Start tracking after 10px horizontal movement
    if (!state.tracking && Math.abs(dx) > 10) {
      state.tracking = true;
    }

    if (!state.tracking) return;

    // Calculate sidebar position based on drag
    if (state.direction === 'open') {
      const x = Math.min(0, Math.max(-SIDEBAR_WIDTH, -SIDEBAR_WIDTH + dx));
      setSidebarDragX(x);
    } else {
      const x = Math.min(0, Math.max(-SIDEBAR_WIDTH, dx));
      setSidebarDragX(x);
    }
  }, []);

  const handleSwipeTouchEnd = useCallback(() => {
    const state = swipeSidebarRef.current;
    if (!state || !state.tracking) {
      swipeSidebarRef.current = null;
      return;
    }

    const elapsed = Date.now() - state.startTime;
    const dragProgress = sidebarDragX !== null ? (sidebarDragX + SIDEBAR_WIDTH) / SIDEBAR_WIDTH : 0;
    const velocity = sidebarDragX !== null
      ? Math.abs((sidebarDragX - (state.direction === 'open' ? -SIDEBAR_WIDTH : 0))) / elapsed
      : 0;

    const shouldOpen = state.direction === 'open'
      ? (dragProgress > SWIPE_COMMIT_RATIO || velocity > SWIPE_VELOCITY_THRESHOLD)
      : (dragProgress > (1 - SWIPE_COMMIT_RATIO) && velocity < SWIPE_VELOCITY_THRESHOLD);

    setShowMobileSidebar(shouldOpen);
    setSidebarDragX(null);
    swipeSidebarRef.current = null;
  }, [sidebarDragX]);

  return (
    <NotificationsProvider>
    {/* LayoutGroup scopes the chat-dock shared-element pair (the header
        launcher + the flight surface share layoutId="chat-surface"). */}
    <LayoutGroup>
    <div
      className={`main-layout${!isMobile ? ' has-app-header' : ''}`}
      onTouchStart={handleSwipeTouchStart}
      onTouchMove={handleSwipeTouchMove}
      onTouchEnd={handleSwipeTouchEnd}
    >
      {/* The nordic/atelier desk (WoodCanvas) is now mounted at the App root so
          it stays stable behind the loading gates (no beige↔wood flash). */}
      {/* Full-width header bar — always spans the top above the rail + chat
          (desktop). The sidebar/modules and chat open below it. */}
      {!isMobile && (
        <header className="app-header">
          <div className="app-header-brand">
            <img src="/favicon.svg" alt="Xavior" className="app-header-logo" />
            <span className="app-header-title">XAVIOR</span>
          </div>
          <div className={`app-header-actions${dockRippleActive ? ' dock-ripple' : ''}`}>
            {/* Docked chat panel lands here, immediately left of the existing
                icons — the group is right-aligned, so they never move. */}
            {/* Stable header order: [Chat launcher] [Bell / Notifications] [Theme].
                The group is right-aligned and items flow in DOM order, so Theme
                stays rightmost and the chat launcher docks in on the far left. */}
            {/* Clear-demo-data pill — self-hides unless demo data is loaded, so
                it only appears while the showcase dataset is present. Wipes the
                entire demo dataset via /demo-seed/purge (same as the chat chip
                and the clear_demo_data agent tool). */}
            <RemoveDemoDataButton />
            <HeaderChatLauncher onLanded={handleChatDockLanded} />
            {/* "Open chat" button — self-gating (module open, no split, not
                mobile). With the split live at all desktop widths (≥768px) the
                sub-split desktop band is gone, so this no longer renders in any
                steady state; kept as the safety affordance for transient
                split-inactive windows. */}
            <HeaderOpenChatButton />
            {/* Desktop (≥768px): the bell is a shared-element morph launcher
                that grows into the Brief and shrinks back (it's gone while open;
                a fixed-size slot keeps Theme from shifting). Mobile (≤767px):
                a plain bell that toggles the centered modal. */}
            {isDesktopViewport ? (
              <HeaderBriefLauncher reviewCount={bellReviewCount} onLanded={handleChatDockLanded} />
            ) : (
              <button
                type="button"
                className="app-header-activities"
                onClick={handleToggleBrief}
                title="Notifications"
                aria-label="Notifications"
                aria-haspopup="dialog"
                aria-expanded={briefOpen}
                aria-controls="daily-brief-panel"
                style={{ position: 'relative' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {bellReviewCount > 0 && (
                  <span className="activities-badge">{bellReviewCount}</span>
                )}
              </button>
            )}
            <button
              type="button"
              className="app-header-activities"
              onClick={() => setThemeOpen(true)}
              title="Theme"
              aria-label="Theme"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a10 10 0 0 0 0 20z" fill="currentColor" stroke="none" />
              </svg>
            </button>
          </div>
        </header>
      )}
      {/* Desktop: icon rail sidebar. Rendered from first paint — including
          during chat-window onboarding — so the rail and chat load together at
          shell-readiness, matching the returning-user reveal (no separate,
          later rail slide-in). */}
      {!isMobile && (
        <SidebarRail
          onNewChat={handleCreateConversation}
          onOpenSettings={() => setIsProfileModalOpen(true)}
          onOpenIntegrations={() => setIsIntegrationsOpen(true)}
          onOpenWorkspaceOptions={() => setIsWorkspaceOptionsOpen(true)}
        />
      )}

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Mobile: keep existing conversation sidebar. Suppressed during
          chat-window onboarding (full-width chat) — see onboardingActive. */}
      {isMobile && (
        <ConversationSidebar
          isOpen={showMobileSidebar && !effectiveOnboardingActive}
          dragX={sidebarDragX}
          onConversationClick={handleConversationClick}
          onCloseSidebar={handleConversationClick}
          onCreateConversation={handleCreateConversation}
        />
      )}

      {/* Mobile sidebar backdrop */}
      {(showMobileSidebar || sidebarDragX !== null) && (
        <div
          className="mobile-sidebar-backdrop"
          onClick={() => setShowMobileSidebar(false)}
          style={sidebarDragX !== null ? {
            opacity: (sidebarDragX + SIDEBAR_WIDTH) / SIDEBAR_WIDTH,
            transition: 'none',
          } : undefined}
        />
      )}

      {/* Unified mobile header — 3-zone: [X logo] [title] [hamburger] */}
      {isMobile && (
        <header className="mobile-header">
          {/* Left: X logo → toggles sidebar */}
          <button
            className="mobile-header-logo-btn"
            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
            aria-label="Toggle sidebar"
          >
            <img src="/favicon.svg" alt="Xavior" />
          </button>

          {/* Center: branding — absolute positioned to center on page (not between icons) */}
          <div className="mobile-header-brand">
            <span className="chat-header-brand">XAVIOR</span>
          </div>

          {/* Right: theme + Notifications bell */}
          <div className="mobile-header-right">
            <button
              className="mobile-header-activity-btn"
              onClick={() => setThemeOpen(true)}
              aria-label="Theme"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a10 10 0 0 0 0 20z" fill="currentColor" stroke="none" />
              </svg>
            </button>
            <button
              className="mobile-header-activity-btn"
              onClick={handleToggleBrief}
              aria-label="Notifications"
              aria-haspopup="dialog"
              aria-expanded={briefOpen}
              aria-controls="daily-brief-panel"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {bellReviewCount > 0 && (
                <span className="activities-badge">{bellReviewCount}</span>
              )}
            </button>
          </div>
        </header>
      )}

      <main className="conversation-content">
        {activeConversation ? (
          <div
            ref={briefPanelContainerRef}
            className={`conversation-chat-panel${
              briefOpen && showBriefCompanion
                ? ' conversation-chat-panel--with-brief'
                : ''
            }`}
          >
            <ChatBox ref={chatBoxRef} isMobile={isMobile} />
            {/* Desktop solo-chat (>=768px, no module): the Daily Brief docks as
                the TOP THIRD of the chat panel — the chat eases down to the bottom
                2/3 beneath it (same idea as the module-open split). Kept mounted
                through the closing flight ('docking') so it can fade out under the
                traveling glass; `dockState` drives the dock classes. */}
            {briefOpen && showBriefCompanion && (
              <ActivityCenter
                mode="panel"
                isOpen={briefOpen}
                dockState={briefDockState}
                cardRef={briefPanelCardRef}
                landingReveal={briefLandingReveal}
                onLandingRevealComplete={ackBriefLandingReveal}
                onClose={closeBrief}
              />
            )}
            {/* The traveling glass surface for the bell ↔ panel morph (solo AND
                module-open split, ≥768px). Portals to <body>; shares its
                layoutId with the header bell — framer FLIPs the surface between
                the bell slot and the panel rect as one unmounts and the other
                mounts (the bell grows into the Brief / the Brief shrinks back). */}
            {isDesktopViewport &&
              (briefDockState === 'docking' || briefDockState === 'undocking') &&
              briefFlightRect && (
                <BriefDockFlight
                  rect={briefFlightRect}
                  mode={briefDockState === 'undocking' ? 'undocking' : 'docking'}
                  onArrived={finishBriefOpen}
                />
              )}
            {/* Module open: the Daily Brief docks as the top third of the chat's
                right column, portaled into the chat-box grid (see ChatBox.css).
                Carries the dock props so it fades out under the traveling glass on
                close and reveals on landing — the bell morph runs here too. */}
            {wantSplitBrief && splitBriefHost && createPortal(
              <div className="chat-brief-pane">
                <ActivityCenter
                  mode="panel"
                  isOpen={briefOpen}
                  dockState={briefDockState}
                  cardRef={briefPanelCardRef}
                  landingReveal={briefLandingReveal}
                  onLandingRevealComplete={ackBriefLandingReveal}
                  onClose={closeBrief}
                />
              </div>,
              splitBriefHost,
            )}
          </div>
        ) : allConversations.length === 0 ? (
          <div className="no-conversation-selected">
            {isReadOnly ? (
              <div className="empty-state loading-state">
                <h2>Shared workspace</h2>
                <p>
                  You have view-only access to this workspace.
                  <br />
                  Select a conversation from the sidebar to explore the shared data.
                </p>
              </div>
            ) : (
              <WelcomePane onNewChat={handleCreateConversation} />
            )}
          </div>
        ) : (
          <div className="no-conversation-selected">
            {isReadOnly ? (
              <div className="empty-state">
                <p>Select a conversation from the sidebar to explore shared data.</p>
              </div>
            ) : (
              <WelcomePane onNewChat={handleCreateConversation} />
            )}
          </div>
        )}
      </main>

      {/* Workspace Options Modal — single user-menu used by both
          desktop SidebarRail (avatar) and mobile sidebar (profile row). */}
      <WorkspaceOptionsModal
        isOpen={isWorkspaceOptionsOpen}
        onClose={() => setIsWorkspaceOptionsOpen(false)}
        onViewArchived={() => setIsArchivedModalOpen(true)}
        onOpenProfile={() => setIsProfileModalOpen(true)}
        onOpenNotifications={() => window.dispatchEvent(new CustomEvent('openNotifications'))}
      />

      {/* Archived Modal */}
      <ArchivedModal
        isOpen={isArchivedModalOpen}
        onClose={() => setIsArchivedModalOpen(false)}
        conversationId={activeConversation?.id}
      />

      {/* Integrations Modal */}
      <IntegrationsModal
        isOpen={isIntegrationsOpen}
        onClose={() => setIsIntegrationsOpen(false)}
      />

      {/* Data Backups Modal */}
      <DataBackupsModal
        isOpen={isDataBackupsOpen}
        onClose={() => setIsDataBackupsOpen(false)}
      />

      {/* Team Modal */}
      <TeamModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        initialTab={teamModalInitialTab}
      />

      {/* Daily Brief: centered modal — narrow / mobile fallback ONLY. Desktop
          solo chat uses the companion card; desktop module-open uses the portaled
          split panel (wantSplitBrief) — both excluded here. */}
      {briefOpen && !showBriefCompanion && !wantSplitBrief && (
        <ActivityCenter isOpen={briefOpen} onClose={closeBrief} />
      )}

      {themeOpen && <ThemeModal onClose={() => setThemeOpen(false)} />}

      {/* Daily Brief window: centered modal controlled by DailyBriefProvider (useDailyBrief).
          Independent surface from the BriefDock/ActivityCenter — coexists with them. */}
      {dailyBrief.isOpen && (
        <DailySummaryModal mode="modal" isOpen onClose={dailyBrief.close} />
      )}

      {/* In-app notification toasts (portals to <body>) — pops a slide-in toast
          when something notifies the user (e.g. an agent finishing a task). */}
      <NotificationToastHost />
      {/* Fires those toasts on real agent-run completions (done/failed). */}
      <AgentRunNotifier />

      {/* Google Sync consent modal — typed "connect gmail" + Gmail/Email
          module Connect buttons + Settings → Connect Google. Routes
          through the full Google OAuth (Gmail R/W, Calendar R/W, Drive
          R/W). Shares the OAuthConsentModal component with the chip
          flow so users see the same modal everywhere a Google
          connection starts. */}
      <OAuthConsentModal
        open={showGoogleSyncModal}
        provider={showGoogleSyncModal ? 'google.full' : null}
        onCancel={() => setShowGoogleSyncModal(false)}
        onConfirm={async () => {
          setShowGoogleSyncModal(false);
          const authToken = token;
          const convId = activeConversation?.id;
          if (!authToken || !convId) return;
          try {
            // Optimistic loading flag — EmailPanel reads this on next mount
            try {
              sessionStorage.setItem('email_oauth_in_progress', 'true');
            } catch {
              /* best-effort */
            }
            // No integrationId → FLOW A (enroll a Google account): picker shows.
            await connectProvider({ provider: 'google', authToken, conversationId: convId });
          } catch {
            try {
              sessionStorage.removeItem('email_oauth_in_progress');
            } catch {
              /* best-effort */
            }
            console.error('Failed to connect to Google');
          }
        }}
      />
    </div>
    </LayoutGroup>
    </NotificationsProvider>
  );
}
